import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout';
import { 
  Search, Building, MapPin, Send, 
  Users, Clock, CheckCircle, MessageSquare,
  Loader2, AlertCircle, ArrowRight, Wine
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, color = 'copper' }) {
  const colors = {
    copper: 'bg-copper/10 text-copper',
    green: 'bg-green-100 text-green-600',
    blue: 'bg-blue-100 text-blue-600',
    yellow: 'bg-yellow-100 text-yellow-600'
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${colors[color]} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-navy">{value}</p>
          <p className="text-sm text-slate-600">{label}</p>
        </div>
      </div>
    </div>
  );
}

export default function BuyerDashboard({ PaymentMessage }) {
  const { user, token, logout } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [requestingId, setRequestingId] = useState(null);
  const [requestMessage, setRequestMessage] = useState('');
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [suppliersRes, requestsRes] = await Promise.all([
        fetch(`${API}/users/suppliers`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/introductions/my-requests`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (suppliersRes.ok) {
        const suppliersData = await suppliersRes.json();
        setSuppliers(suppliersData);
      }

      if (requestsRes.ok) {
        const requestsData = await requestsRes.json();
        setMyRequests(requestsData);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestIntro = async () => {
    if (!selectedSupplier) return;
    
    setRequestingId(selectedSupplier.id);
    try {
      const res = await fetch(`${API}/introductions/request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          supplier_id: selectedSupplier.id,
          message: requestMessage
        })
      });

      if (res.ok) {
        setShowRequestModal(false);
        setRequestMessage('');
        setSelectedSupplier(null);
        fetchData(); // Refresh data
      } else {
        const data = await res.json();
        alert(data.detail || 'Failed to request introduction');
      }
    } catch (err) {
      console.error('Error requesting intro:', err);
    } finally {
      setRequestingId(null);
    }
  };

  const openRequestModal = (supplier) => {
    setSelectedSupplier(supplier);
    setShowRequestModal(true);
  };

  const filteredSuppliers = suppliers.filter(s => 
    s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.company?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.state?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Check if already requested
  const hasRequested = (supplierId) => {
    return myRequests.some(r => r.supplier_id === supplierId);
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-copper" />
        </div>
      </Layout>
    );
  }

  const pendingRequests = myRequests.filter(r => r.status === 'pending').length;
  const approvedRequests = myRequests.filter(r => r.status === 'approved').length;

  return (
    <Layout>
      <div className="bg-slate-50 min-h-screen">
        <div className="container-custom py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-navy">
                Buyer Dashboard
              </h1>
              <p className="text-slate-600 mt-1">Welcome back, {user?.name}</p>
            </div>
            <button
              onClick={logout}
              className="text-sm text-slate-600 hover:text-copper"
            >
              Sign Out
            </button>
          </div>

          <PaymentMessage />

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Wine} label="Available Suppliers" value={suppliers.length} color="copper" />
            <StatCard icon={Send} label="Intro Requests" value={myRequests.length} color="blue" />
            <StatCard icon={Clock} label="Pending" value={pendingRequests} color="yellow" />
            <StatCard icon={CheckCircle} label="Approved" value={approvedRequests} color="green" />
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Browse Suppliers */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-semibold text-navy text-lg">Browse Suppliers</h2>
                  <Link to="/target-list" className="text-copper text-sm hover:text-copper-dark flex items-center gap-1">
                    View Target List <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>

                {/* Search */}
                <div className="relative mb-6">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name, company, or state..."
                    className="input-field pl-11"
                  />
                </div>

                {filteredSuppliers.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Wine className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No suppliers found</p>
                    <p className="text-sm mt-1">Check back soon for new suppliers</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredSuppliers.map((supplier) => (
                      <div
                        key={supplier.id}
                        className="border border-slate-200 rounded-xl p-4 hover:border-copper/30 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-medium text-navy">{supplier.company || supplier.name}</h3>
                            <div className="flex items-center gap-4 mt-1 text-sm text-slate-600">
                              <span className="flex items-center gap-1">
                                <Building className="w-4 h-4" />
                                {supplier.name}
                              </span>
                              {supplier.state && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-4 h-4" />
                                  {supplier.state}
                                </span>
                              )}
                            </div>
                            {supplier.bio && (
                              <p className="text-sm text-slate-500 mt-2 line-clamp-2">{supplier.bio}</p>
                            )}
                          </div>
                          <button
                            onClick={() => openRequestModal(supplier)}
                            disabled={hasRequested(supplier.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                              hasRequested(supplier.id)
                                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                : 'bg-copper text-white hover:bg-copper-dark'
                            }`}
                          >
                            {hasRequested(supplier.id) ? 'Requested' : (
                              <>
                                <MessageSquare className="w-4 h-4" />
                                Request Intro
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* My Requests */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h2 className="font-semibold text-navy text-lg mb-6">My Requests</h2>

                {myRequests.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Send className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No requests yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {myRequests.map((request) => (
                      <div
                        key={request.id}
                        className="border border-slate-200 rounded-lg p-3"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-navy text-sm">{request.supplier_company}</p>
                            <p className="text-xs text-slate-500">{request.supplier_name}</p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            request.status === 'approved' ? 'bg-green-100 text-green-700' :
                            request.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            request.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {request.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Request Modal */}
      {showRequestModal && selectedSupplier && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="font-semibold text-navy text-lg mb-2">
              Request Introduction
            </h3>
            <p className="text-slate-600 text-sm mb-4">
              to <strong>{selectedSupplier.company || selectedSupplier.name}</strong>
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-navy mb-2">
                Message (optional)
              </label>
              <textarea
                value={requestMessage}
                onChange={(e) => setRequestMessage(e.target.value)}
                rows={3}
                className="input-field resize-none"
                placeholder="Tell them about your business and what you're looking for..."
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRequestModal(false);
                  setSelectedSupplier(null);
                  setRequestMessage('');
                }}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRequestIntro}
                disabled={requestingId === selectedSupplier.id}
                className="flex-1 btn-copper justify-center"
              >
                {requestingId === selectedSupplier.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Send Request'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
